import {
  CharityHousehold,
  CharitySessionStatus,
  CreateHouseholdResponse,
  HouseholdSignedInResponse,
} from "../types";

interface CharitySessionStatusInput {
  sessionToken: string | null;
  household: CharityHousehold | null;
  isHouseholdRestorePending: boolean;
}

/**
 * Resolves the one session status every charity screen reads.
 *
 * A token with no household yet is only signed out once its restore has been
 * attempted and failed; until then it is restoring, which is what keeps a
 * participant-only page from calling a reload a sign-out.
 *
 * @param sessionToken - The stored bearer token, or null when there is none
 * @param household - The household already loaded for that token
 * @param isHouseholdRestorePending - Whether a household restore is still owed
 * @returns The status the header and the session gate render from
 */
export const resolveCharitySessionStatus = ({
  sessionToken,
  household,
  isHouseholdRestorePending,
}: CharitySessionStatusInput): CharitySessionStatus => {
  if (sessionToken === null) {
    return CharitySessionStatus.SignedOut;
  }
  if (household !== null) {
    return CharitySessionStatus.SignedIn;
  }
  return isHouseholdRestorePending ? CharitySessionStatus.Restoring : CharitySessionStatus.SignedOut;
};

/**
 * Whether creating a household handed the session over rather than mailing it.
 *
 * The invitation token is what earns that, so this is also the test for whether
 * the join came in off the invitation mail or off a cold visit to the form.
 *
 * @param response - What creating the household answered with
 * @returns True when the participant is already signed in
 */
export const isHouseholdSignedInResponse = (
  response: CreateHouseholdResponse
): response is HouseholdSignedInResponse =>
  typeof (response as HouseholdSignedInResponse).sessionToken === "string";
